import { AnalyticsObjectProvider } from '@utils/analytics/object';
import { renderShopifyArtistName } from '@utils/artists';
import { storyblokProductReleaseType } from '@utils/storyblokReleaseType';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { usePreviousDistinct } from 'react-use';

import { csrClient } from '../../../apollo/api/avantArteClient';
import { storefrontClient } from '../../../apollo/shopify/storefrontClient';
import { UserPreferences } from '../../../context/UserPreferencesContext';
import AvantArteAuthGateway from '../../../gateways/AvantArteAuthGateway';
import { AvantArteProductGateway } from '../../../gateways/AvantArteProductGateway';
import { ShopifyCheckoutGateway } from '../../../gateways/ShopifyCheckoutGateway';
import { ShopifyProductsGateway } from '../../../gateways/ShopifyProductsGateway';
import { getProductStatus } from '../../../helpers/purchasing';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useCurrency } from '../../../hooks/useCurrency';
import { useProductBasketPurchasing } from '../../../hooks/useProductBasketPurchasing';
import { useUnitOfMeasurement } from '../../../hooks/useUnitOfMeasurement';
import { GeneratedPrivateLink } from '../../../types/generated';
import { ProductV2Storyblok } from '../../../types/generated-storyblok';
import {
  CollectionSetProduct,
  DrawerFormType,
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { AnalyticsEmitter } from '../../../utils/analytics/emitter';
import { framedByDefault } from '../../../utils/frames';
import Drawer from '../../Layout/Drawer';
import { LocaleForm } from '../../Layout/Footer/LocaleForm';
import EmbeddedContainer from '../DrawerContainer/EmbeddedContainer';
import { BasketReviewForm } from './BasketReviewForm';
import CollectionSetForm from './CollectionSetForm';
import ReviewForm from './ReviewForm';
import { ReviewFrameForm } from './ReviewFrameForm';

export type Props<T extends ProductPurchaseDetails> = {
  analyticsEmitter?: AnalyticsEmitter;
  overlayOtherDrawers?: boolean;
  privateLink?: GeneratedPrivateLink;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
  setVisibleForm(visibleForm: DrawerFormType | undefined): void;
  showCrossSell?: boolean;
  visibleForm: DrawerFormType | undefined;
  isEnquiry?: boolean;
};

const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const productsGateway = new ShopifyProductsGateway(storefrontClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);

const ReviewCheckoutDrawer: React.FC<Props<ProductPurchaseDetails>> = ({
  analyticsEmitter,
  isEnquiry,
  overlayOtherDrawers = false,
  privateLink,
  product,
  purchasingController,
  setVisibleForm,
  showCrossSell,
  visibleForm,
}) => {
  const { t: footerT } = useTranslation('footer');
  const { t } = useTranslation(['product', 'countries']);
  const [formTitle, setFormTitle] = React.useState<string>();

  const [currency] = useCurrency();
  const [unit] = useUnitOfMeasurement();
  const [countryCode] = useCountryCode();
  const shouldReduceMotion = useReducedMotion();
  const animationProps = {
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: '-16px' },
    initial: { opacity: 0, x: '16px' },
    transition: { type: 'tween', duration: shouldReduceMotion ? 0 : 0.12 },
  };

  const form = useForm<Omit<UserPreferences, 'cookieConsent'>>({
    defaultValues: {
      countryCode,
      currency,
      unitOfMeasurement: unit,
    },
  });

  const frameIncluded = framedByDefault(
    purchasingController.product?.framingOffered,
  );

  useEffect(() => {
    switch (visibleForm) {
      case 'locale':
        setFormTitle(footerT('drawers.locale.title'));
        break;
      case 'frame':
        setFormTitle(t('drawers.review.framing.title'));
        break;
      case 'releases':
        setFormTitle(t('drawers.review.releases.title'));
        break;
      case 'review':
        setFormTitle(t('drawers.review.checkout.title'));
        break;
      default:
        setFormTitle(undefined);
        break;
    }
  }, [visibleForm]);

  const goToReleases = () => {
    setVisibleForm('releases');
  };

  const goToFraming = () => {
    setVisibleForm('frame');
  };

  const goToReview = () => {
    setVisibleForm('review');
  };

  const closeDrawer = () => {
    setVisibleForm(undefined);
  };

  const forms = [
    purchasingController.collectionSet && 'releases',
    !frameIncluded && purchasingController.frameOptions?.length && 'frame',
    'review',
  ].filter(Boolean);
  const showBackButton = visibleForm !== forms[0];

  const [{ id: mainProductId }] =
    product.component === 'product_v2'
      ? [{ id: (product as unknown as ProductV2Storyblok).shopifyId }]
      : product.id;
  const previousMainProductId = usePreviousDistinct(mainProductId);

  const basket = useProductBasketPurchasing({
    authGateway,
    checkoutGateway,
    productsGateway,
    aaProductsGateway,
    release: purchasingController.release,
    privateLinkIdentifier: privateLink?.slug,
    variantIdentifier:
      purchasingController.product?.shopifyVariantId || undefined,
    initialProductToAddToBasket: {
      productId: { id: mainProductId },
      quantity: purchasingController.quantity,
      unlockCode: purchasingController.unlockCode,
    },
  });

  // synchronize the main product from purchasingController to the basket
  // important in private rooms where you can cycle through multiple products
  useEffect(() => {
    if (!previousMainProductId || previousMainProductId === mainProductId) {
      return;
    }
    const previousMainProductEntryId = basket.findEntryIdByProductId({
      id: previousMainProductId,
    });
    if (!previousMainProductEntryId) {
      return;
    }
    basket.removeProductFromBasket(previousMainProductEntryId);
    basket.addProductToBasket({
      productId: { id: mainProductId },
      quantity: purchasingController.quantity,
      unlockCode: purchasingController.unlockCode,
    });
  }, [mainProductId, previousMainProductId]);

  // synchronize the frames from the purchasingController to the basket
  useEffect(() => {
    const entryId = basket.findEntryIdByProductId({ id: mainProductId });
    if (!entryId) {
      return;
    }
    basket.removeFrameFromProduct(entryId);
    if (purchasingController.frame) {
      basket.addFrameToProduct(entryId, purchasingController.frame);
    }
  }, [purchasingController.frame]);

  return (
    <Drawer
      backButtonAction={() => {
        if (visibleForm === 'review') {
          goToFraming();
          return;
        }

        if (visibleForm === 'frame') {
          goToReleases();
          return;
        }

        goToReview();
      }}
      onClose={closeDrawer}
      overlayOtherDrawers={overlayOtherDrawers}
      showBackButton={showBackButton}
      shown={Boolean(visibleForm)}
      title={formTitle}
    >
      <div>
        <AnimatePresence exitBeforeEnter>
          {visibleForm === 'review' ? (
            <motion.div key="review" {...animationProps}>
              {showCrossSell ? (
                <BasketReviewForm
                  basket={basket}
                  formIsVisible={Boolean(visibleForm)}
                  frameIncluded={frameIncluded}
                  frameOptions={purchasingController.frameOptions}
                  hasNoOrderCancellation={
                    purchasingController.product?.noOrderCancellation || false
                  }
                  privateLink={privateLink}
                  product={product}
                  setFormTitle={setFormTitle}
                  setVisibleForm={setVisibleForm}
                  showTitle
                  visibleForm={visibleForm}
                />
              ) : (
                <ReviewForm
                  analyticsEmitter={analyticsEmitter}
                  drawerType="checkout"
                  privateLink={privateLink}
                  product={product}
                  purchasingController={purchasingController}
                  setVisibleForm={setVisibleForm}
                  shown={visibleForm === 'review'}
                />
              )}
            </motion.div>
          ) : null}

          {visibleForm === 'locale' ? (
            <motion.div key="locale" {...animationProps}>
              <EmbeddedContainer embedded>
                <LocaleForm form={form} hideForm={goToReview} />
              </EmbeddedContainer>
            </motion.div>
          ) : null}

          {visibleForm === 'frame' ? (
            <motion.div key="frame" {...animationProps}>
              <ReviewFrameForm
                onCloseDrawer={closeDrawer}
                onSubmit={isEnquiry ? closeDrawer : goToReview}
                product={product}
                purchasingController={purchasingController}
              />
            </motion.div>
          ) : null}

          {visibleForm === 'releases' ? (
            <motion.div key="releases" {...animationProps}>
              <AnalyticsObjectProvider
                name="product page"
                properties={{
                  'product_title': product.title,
                  'artist name': renderShopifyArtistName(
                    purchasingController.product?.artist ?? '',
                  ),
                  'product_release_type': storyblokProductReleaseType(product),
                  'product_release_date': product.launchDate,
                  'product_frame_chosen': Boolean(purchasingController.frame),
                  'product_status': getProductStatus(
                    product,
                    purchasingController,
                  ),
                  'product_medium': product?.medium,
                  'product_price': purchasingController.product?.price,
                  'product_edition_size': product?.editionSize,
                  'product_id': product?.id,
                  'product_can_be_framed': Boolean(
                    purchasingController.frameOptions?.length,
                  ),
                  'product_frame_upgrade_available': Boolean(
                    purchasingController.frameOptions,
                  ),
                  'product_frame_glazing':
                    purchasingController.frame?.variant.title,
                  ecommerce: {
                    items: [
                      {
                        item_id: product?.id,
                        item_name: product.title,
                        item_brand: renderShopifyArtistName(
                          purchasingController.product?.artist ?? '',
                        ),
                        item_category: product.medium?.join(','),
                        price: purchasingController.product?.price,
                        quantity: purchasingController.quantity,
                      },
                    ],
                  },
                }}
              >
                {() => (
                  <CollectionSetForm
                    description={purchasingController.collectionSetDescription}
                    items={
                      purchasingController.collectionSet as CollectionSetProduct[]
                    }
                    onSubmit={
                      !frameIncluded &&
                      purchasingController.frameOptions?.length
                        ? goToFraming
                        : goToReview
                    }
                    selectAll={() =>
                      purchasingController.collectionSetToggleAll()
                    }
                    toggleItem={(id) =>
                      purchasingController.collectionSetToggleId(id)
                    }
                  />
                )}
              </AnalyticsObjectProvider>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </Drawer>
  );
};

export default ReviewCheckoutDrawer;
